/**
 * QR code — pour que les élèves n'aient rien à taper.
 *
 * Le QR est **projeté** par l'enseignant ; chaque élève le vise avec
 * l'appareil photo de son téléphone, qui ouvre directement l'adresse du vote.
 * Aucune saisie, donc aucune faute de recopie, et pas de file d'attente.
 *
 * Le tracé est un SVG **en ligne** : pas d'image en `data:`, pas de toile, et
 * un rendu net à toute taille — utile quand on projette.
 */

import qrcode from 'qrcode-generator';
import { html, type TemplateResult } from 'lit';

export type NiveauCorrection = 'L' | 'M' | 'Q' | 'H';

/**
 * Matrice des modules du QR code : `true` pour un module sombre.
 * Fonction pure, sans DOM — c'est elle que les tests vérifient en la décodant.
 */
export function matriceQr(texte: string, correction: NiveauCorrection = 'M'): boolean[][] {
  const qr = qrcode(0, correction);
  qr.addData(texte);
  qr.make();
  const cote = qr.getModuleCount();
  const matrice: boolean[][] = [];
  for (let ligne = 0; ligne < cote; ligne += 1) {
    const rangee: boolean[] = [];
    for (let colonne = 0; colonne < cote; colonne += 1) {
      rangee.push(qr.isDark(ligne, colonne));
    }
    matrice.push(rangee);
  }
  return matrice;
}

export interface OptionsQr {
  /** Modules de silence autour du code. Quatre est la valeur normalisée. */
  readonly marge?: number;
  readonly correction?: NiveauCorrection;
}

/**
 * Rend un QR code en SVG. Un seul tracé pour tous les modules sombres : le
 * document reste léger, même pour une adresse longue.
 */
export function qrSvg(texte: string, options: OptionsQr = {}): TemplateResult {
  const matrice = matriceQr(texte, options.correction ?? 'M');
  const marge = options.marge ?? 4;
  const modules = matrice.length;
  const cote = modules + marge * 2;

  let chemin = '';
  for (let ligne = 0; ligne < modules; ligne += 1) {
    const rangee = matrice[ligne] as boolean[];
    for (let colonne = 0; colonne < modules; colonne += 1) {
      if (rangee[colonne] === true) {
        chemin += `M${colonne + marge} ${ligne + marge}h1v1h-1z`;
      }
    }
  }

  return html`<svg
    class="qr"
    viewBox="0 0 ${cote} ${cote}"
    role="img"
    aria-label="QR code à scanner pour ouvrir la page de vote"
    shape-rendering="crispEdges"
  >
    <rect width=${cote} height=${cote} fill="#ffffff"></rect>
    <path d=${chemin} fill="#000000"></path>
  </svg>`;
}

/** Le SVG seul, pour l'incorporer dans une page imprimée. */
export function qrSvgTexte(texte: string, options: OptionsQr = {}): string {
  const matrice = matriceQr(texte, options.correction ?? 'M');
  const marge = options.marge ?? 4;
  const modules = matrice.length;
  const cote = modules + marge * 2;
  let chemin = '';
  for (let ligne = 0; ligne < modules; ligne += 1) {
    const rangee = matrice[ligne] as boolean[];
    for (let colonne = 0; colonne < modules; colonne += 1) {
      if (rangee[colonne] === true) chemin += `M${colonne + marge} ${ligne + marge}h1v1h-1z`;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${cote} ${cote}" width="360" height="360" shape-rendering="crispEdges"><rect width="${cote}" height="${cote}" fill="#ffffff"/><path d="${chemin}" fill="#000000"/></svg>`;
}
